/**
 * useTutorLiveCenterActions — les gestes du masteur sur l'activité au centre : « Montrer à l'élève »,
 * « Soumettre », « Valider », « Redonner », « Modifier le titre », « Archiver », et « Retirer » le quiz
 * d'abord ; et de quoi faire une semblable de l'activité ouverte (`similarSource`, que lit « + Activité »).
 *
 * Le legacy est `student/activity/todo/TodoModal.js` (branche tuteur, `noModal`), avec
 * `components/ShowToStudentButton.js`, `EditActivityTitleModal.js` et `components/QuizFirstMention.js`.
 * **Protocole identique** (décision 7 du câblage) : les mêmes services avec les mêmes corps, les mêmes
 * messages de salle avec les mêmes champs — `TUTOR_LIVE_CENTER_EQUIVALENCE` les tient, une ligne par
 * geste, un test par ligne. Une différence voulue : l'archivage n'envoie `deleteTodo` qu'**une** fois
 * (l'ancienne fiche de gauche, montée à côté, en renvoyait un second, C-47).
 *
 * Les boîtes (« Valider l'activité », « Impossible », archiver, interrompre le champion) partent dans le
 * dialogue `custom` du calque, la saisie du titre dans sa fente `sheet` ; les accusés dans sa snackbar
 * (`notify`), sans les ✅ / ⚠️ de l'ancien : la snackbar du livre porte son ton.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import type React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../../../../data/services/activitiesService"
import { liveService } from "../../../../../../data/services/liveService"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { studentsService } from "../../../../../../data/services/studentsService"
import { buildPendingAttachmentsBody } from "../../../../../../data/utility/pendingAttachmentsUtils"
import { apiCall, apiFailureOf, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type UITutorActivityAction } from "../../../types/tutorEleves"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import {
    finalizeSilentBody,
    lastConversationId,
    redoLiveActivityBody,
    tutorFinalizedActivityPayload,
    type ApiLiveActivity,
    type ApiLiveStudent,
} from "../../../../adapters/tutor/Live/tutorLiveActivityAdapter"
import { type TutorLiveLayer, type TutorLiveRoomContext } from "./tutorLiveContract"
import { renderTitleEditor } from "./useTutorLiveCenterSurfaces"

/** Ce qu'un geste envoie : ses services (dans l'ordre), le message de salle et ses champs. */
export type TutorLiveCenterEquivalence = {
    gesture: string
    services: string[]
    message: string | null
    fields: string[]
    /** Ce que la salle fait du message quand elle n'est pas connectée (`roomSender`). */
    policy?: "keep" | "drop"
}

/**
 * La table d'équivalence du centre : geste → service(s) → message de salle (nom et champs exacts du
 * legacy). `useTutorLiveCenter.equivalence.hook.test.tsx` joue chaque ligne.
 */
export const TUTOR_LIVE_CENTER_EQUIVALENCE: TutorLiveCenterEquivalence[] = [
    {
        gesture: "open",
        services: ["getActivities"],
        message: null,
        fields: [],
    },
    {
        gesture: "show",
        services: [],
        message: "newTodo",
        fields: ["id", "activity", "forceFocus"],
        // Un « Montrer » arrivé après la reconnexion ferait sauter l'élève sans qu'on l'ait voulu à ce moment.
        policy: "drop",
    },
    {
        gesture: "show_interrupt_helper",
        services: ["finishConversation"],
        message: "newTodo",
        fields: ["id", "activity", "forceFocus"],
        policy: "drop",
    },
    {
        gesture: "submit",
        services: ["finalizeActivitySilent", "getStudent", "getActivities"],
        message: "tutorFinalizedActivity",
        fields: [
            "studentId",
            "activityId",
            "activityTitle",
            "xpGained",
            "petals",
            "finishedAt",
            "status",
            "duration",
            "xpGainedActivity",
        ],
        policy: "keep",
    },
    { gesture: "validate", services: ["tutorValidateActivity", "getActivities"], message: null, fields: [] },
    {
        gesture: "redo",
        services: ["activityTodoAsTutor"],
        message: "newTodo",
        fields: ["id", "activity"],
        policy: "keep",
    },
    {
        gesture: "archive",
        services: ["getActivity", "deleteActivitiesTodo"],
        message: "deleteTodo",
        fields: ["id", "activity_ids"],
        policy: "keep",
    },
    {
        gesture: "editTitle",
        services: ["updateActivityTodo"],
        message: "newTodo",
        fields: ["id", "activity"],
        policy: "keep",
    },
    {
        gesture: "removeQuiz",
        services: ["deletePrerequisiteQuiz", "getLiveSessionV2"],
        message: "newTodo",
        fields: ["id", "activity"],
        policy: "keep",
    },
    {
        gesture: "correctionReview",
        services: ["submitActivityCorrectionFeedback"],
        message: null,
        fields: [],
    },
    { gesture: "quizFeedback", services: ["giveQuizFeedback"], message: null, fields: [] },
    { gesture: "signalQuestion", services: ["signalQuizQuestion"], message: null, fields: [] },
    { gesture: "generateVisual", services: ["generateVisualExplanation", "getMaterial"], message: null, fields: [] },
    { gesture: "visualFeedback", services: ["submitMaterialFeedback"], message: null, fields: [] },
    { gesture: "aidRetry", services: ["retryMaterialCorrection"], message: null, fields: [] },
    { gesture: "aidFeedback", services: ["submitMaterialFeedback"], message: null, fields: [] },
]

export type CenterNotify = (message: string, tone: "success" | "danger" | "info") => void

/** Après « Montrer à l'élève », la roue tourne jusqu'à ce qu'il l'ait devant lui, ou ce délai. */
export const SHOW_TO_STUDENT_TIMEOUT_MS = 5000

export const CENTER_TEXTS = {
    alreadyShown: "L'élève l'a déjà devant lui.",
    interruptTitle: (name: string, helperName: string) => `${name} est en plein point d'avancement avec ${helperName}`,
    interruptMessage: "Souhaites-tu interrompre la conversation pour lui montrer cette activité ?",
    interruptQuizTitle: (name: string) => `${name} est en plein quiz de prérequis`,
    interruptQuizMessage: "Souhaites-tu interrompre son quiz pour lui montrer cette activité ?",
    interruptConfirm: "Oui, interrompre",
    no: "Non",
    submitted: (firstname: string | null | undefined) => `Activité soumise pour ${firstname || "l'élève"}`,
    validateTitle: "Valider l'activité",
    validateMessage: "Veux-tu marquer cette activité comme validée ?",
    validateConfirm: "Valider",
    back: "Retour",
    redone: "La même activité a été ajoutée à la liste de l'élève",
    lockedTitle: "Impossible",
    lockedMessage: "L'élève a actuellement ouvert cette activité.",
    archiveTitle: "Veux-tu archiver cette activité ?",
    archiveMessage: "Tu pourras la restaurer depuis les activités archivées.",
    archiveConfirm: "Archiver",
    cancel: "Annuler",
    emptyTitle: "Le titre ne peut pas être vide",
    titleSaved: "Titre modifié avec succès",
    quizRemoved: (name: string) => `Quiz retiré : ${name} voit l'énoncé`,
    quizRemovedLater: (name: string) => `Quiz retiré : ${name} verra l'énoncé en rouvrant son activité`,
    quizRemoveFailed: "Le quiz n'a pas pu être retiré, réessaie.",
    failed: "Une erreur est survenue. Réessaie dans un instant.",
}

/** La source d'une génération « à partir de l'exo » : ce que la feuille de génération du fil reçoit. */
export type TutorLiveGenerateSource = {
    studentId: string
    activityId: string
    materialId: string | number
    /** Le titre de l'exercice source (« Générer à partir de l'exo « … » »). */
    title: string | null
    /** Son niveau : le Type par défaut. */
    level: number | null
    /** La confiance de son chapitre (`analysis.confidence`) : en dessous de `high`, son chapitre se choisit d'abord. */
    confidence: string | null
    /** Sa matière (`school_subject_id`), dont on liste les chapitres. */
    subjectId: string | number | null
    /** Sa classe (`class`), quand celle de l'élève manque. */
    className: string | null
    /** Ce que la relecture montre de l'original, à côté de la nouvelle : son énoncé, la copie de l'élève. */
    preview?: { statement?: string; imageUri?: string }
}

type Dialog = NonNullable<TutorLiveLayer["dialog"]>

export type UseTutorLiveCenterActionsArgs = {
    room: TutorLiveRoomContext
    student: ApiLiveStudent | null
    raw: ApiLiveActivity | null
    /** L'élève a l'activité devant lui (« Montrée à l'élève ✓ »). */
    shown: boolean
    /** L'élève est avec le champion (`ongoingCheckpointIds`). */
    withHelper: boolean
    /** …et ce checkpoint est son quiz de prérequis (`quizParticipantIds`) : la boîte dit quiz. */
    inQuiz?: boolean
    meId: number | null
    /** Le modèle et la matière connus du catalogue (« Redonner »). */
    known: { activityTemplateId?: string | number | null; schoolSubjectId?: string | number | null }
    /** Les pièces pas encore confirmées de l'activité (`pendingAttachments.byActivityId`). */
    pendingAttachments: unknown
    /** Relit l'activité (`getActivities with_logs`). */
    refresh: () => void
    /** Ferme l'activité au centre (archivée). */
    close: () => void
    notify: CenterNotify
}

export type TutorLiveCenterActions = {
    onAction: (action: UITutorActivityAction) => void
    submitting: UITutorActivityAction | null
    dialog: Dialog | null
    sheet: React.ReactNode
    quizFirst: { onRemove: () => void; removing: boolean }
    /** L'activité ouverte, de quoi en faire une semblable (« /similaire » du fil) ; `null` sans support. */
    similarSource: () => TutorLiveGenerateSource | null
}

export function useTutorLiveCenterActions(args: UseTutorLiveCenterActionsArgs): TutorLiveCenterActions {
    const dispatch = useDispatch() as unknown as (action: never) => unknown
    const { raw, shown } = args
    const [submitting, setSubmitting] = useState<UITutorActivityAction | null>(null)
    const [dialog, setDialog] = useState<Dialog | null>(null)
    const [title, setTitle] = useState<{ value: string; saving: boolean } | null>(null)
    const [removing, setRemoving] = useState(false)
    const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Le dernier état lu, pour les gestes qui reviennent après une réponse.
    const latest = useRef(args)
    latest.current = args

    const call = useCallback(
        <T = unknown>(service: unknown, params: Parameters<typeof apiCall>[2] = {}) =>
            apiCall<T>(dispatch, service as ApiService, params),
        [dispatch]
    )
    const send = useCallback(
        (type: string, data: Record<string, unknown>, to: number, policy: "keep" | "drop") =>
            latest.current.room.live?.send(type, data, [to], policy) ??
            Promise.resolve({ ok: false as const, reason: "dropped" as const }),
        []
    )
    const fail = useCallback(
        (cause: unknown) => latest.current.notify(apiErrorMessage(apiFailureOf(cause), CENTER_TEXTS.failed), "danger"),
        []
    )

    // ── « Montrer à l'élève » ────────────────────────────────────────────────────────────────────
    const stopShow = useCallback(() => {
        if (showTimer.current != null) clearTimeout(showTimer.current)
        showTimer.current = null
        setSubmitting((was) => (was === "show" ? null : was))
    }, [])
    useEffect(() => {
        if (shown) stopShow()
    }, [shown, stopShow])
    useEffect(
        () => () => {
            if (showTimer.current != null) clearTimeout(showTimer.current)
        },
        []
    )

    const sendShow = useCallback(() => {
        const { student: who, raw: activity } = latest.current
        if (who?.id == null || activity == null) return
        if (showTimer.current != null) clearTimeout(showTimer.current)
        setSubmitting("show")
        showTimer.current = setTimeout(stopShow, SHOW_TO_STUDENT_TIMEOUT_MS)
        // Aucun message d'échec, comme l'ancien : un envoi refusé arrête seulement la roue.
        void send("newTodo", { id: who.id, activity, forceFocus: true }, who.id, "drop").then((result) => {
            if (result != null && result.ok === false) stopShow()
        })
    }, [send, stopShow])

    const show = useCallback(() => {
        const { shown: already, withHelper, inQuiz, student: who, room: salle } = latest.current
        if (already) {
            latest.current.notify(CENTER_TEXTS.alreadyShown, "info")
            return
        }
        if (!withHelper) {
            sendShow()
            return
        }
        // Afficher une activité ferme l'échange de l'élève avec le champion : on demande d'abord.
        const name = who?.display_name ?? who?.firstname ?? ""
        setDialog({
            value: {
                kind: "custom",
                title: inQuiz
                    ? CENTER_TEXTS.interruptQuizTitle(name)
                    : CENTER_TEXTS.interruptTitle(name, salle.helperName),
                message: inQuiz ? CENTER_TEXTS.interruptQuizMessage : CENTER_TEXTS.interruptMessage,
                confirmLabel: CENTER_TEXTS.interruptConfirm,
                cancelLabel: CENTER_TEXTS.no,
            },
            onConfirm: () => {
                setDialog(null)
                amplitude.track("checkpoint.interrupt.accept")
                const conversationId = lastConversationId(latest.current.student)
                if (conversationId != null) {
                    call(liveService.finishConversation, {
                        routeParams: { conversation_id: conversationId },
                        data: { interruption_reason: "tutor_interruption", ended_at: new Date().toISOString() },
                    }).catch(() => undefined)
                }
                sendShow()
            },
            onCancel: () => {
                setDialog(null)
                amplitude.track("checkpoint.interrupt.reject")
            },
            onDismiss: () => setDialog(null),
        })
    }, [call, sendShow])

    // ── « Soumettre » ──────────────────────────────────────────────────────────────────────────────
    const submit = useCallback(() => {
        const { raw: activity, student: who, pendingAttachments } = latest.current
        if (activity?.id == null || who?.id == null) return
        const pending = buildPendingAttachmentsBody(
            (pendingAttachments as Record<string, unknown> | null | undefined)?.[String(activity.id)]
        ) as unknown[] | undefined
        setSubmitting("submit")
        call<Parameters<typeof tutorFinalizedActivityPayload>[2]>(activitiesService.finalizeActivitySilent, {
            routeParams: { id: activity.id },
            data: finalizeSilentBody(activity, pending),
        })
            .then((answer) => {
                latest.current.notify(CENTER_TEXTS.submitted(who.firstname), "success")
                call(studentsService.getStudent, { routeParams: { id: who.id } }).catch(() => undefined)
                void send(
                    "tutorFinalizedActivity",
                    tutorFinalizedActivityPayload(who.id!, activity, answer),
                    who.id!,
                    "keep"
                )
                latest.current.refresh()
            })
            .catch(fail)
            .finally(() => setSubmitting(null))
    }, [call, fail, send])

    // ── « Valider » ────────────────────────────────────────────────────────────────────────────────
    const validate = useCallback(() => {
        setDialog({
            value: {
                kind: "custom",
                title: CENTER_TEXTS.validateTitle,
                message: CENTER_TEXTS.validateMessage,
                confirmLabel: CENTER_TEXTS.validateConfirm,
                cancelLabel: CENTER_TEXTS.back,
            },
            onConfirm: () => {
                setDialog(null)
                const { raw: activity, meId } = latest.current
                if (activity?.id == null) return
                setSubmitting("validate")
                // `reviewer` part comme dans l'ancien ; le service l'ignore (`activitiesService:73`).
                call(activitiesService.tutorValidateActivity, { routeParams: { id: activity.id, reviewer: meId } })
                    .then(() => latest.current.refresh())
                    .catch(fail)
                    .finally(() => setSubmitting(null))
            },
            onCancel: () => setDialog(null),
            onDismiss: () => setDialog(null),
        })
    }, [call, fail])

    // ── « Redonner » ───────────────────────────────────────────────────────────────────────────────
    const redo = useCallback(() => {
        const { raw: activity, student: who, known } = latest.current
        if (activity == null || who?.id == null) return
        setSubmitting("redo")
        call<Record<string, unknown>>(activitiesService.activityTodoAsTutor, {
            routeParams: { id: who.id },
            data: redoLiveActivityBody(activity, known),
        })
            .then((answer) => {
                latest.current.notify(CENTER_TEXTS.redone, "success")
                void send("newTodo", { id: who.id, activity: answer }, who.id!, "keep")
            })
            .catch(fail)
            .finally(() => setSubmitting(null))
    }, [call, fail, send])

    // ── « Archiver » ───────────────────────────────────────────────────────────────────────────────
    const archive = useCallback(() => {
        const { raw: activity, student: who } = latest.current
        if (activity?.id == null || who?.id == null) return
        setSubmitting("archive")
        // L'ancien relit l'activité avant de demander : verrouillée par l'élève, on refuse.
        call<{ locked_at?: string | null; student_id?: number | null }>(activitiesService.getActivity, {
            routeParams: { id: activity.id },
        })
            .then((fresh) => {
                setSubmitting(null)
                if (fresh?.locked_at && fresh?.student_id !== latest.current.meId) {
                    setDialog({
                        value: {
                            kind: "custom",
                            title: CENTER_TEXTS.lockedTitle,
                            message: CENTER_TEXTS.lockedMessage,
                            confirmLabel: "OK",
                        },
                        onConfirm: () => setDialog(null),
                        onDismiss: () => setDialog(null),
                    })
                    return
                }
                setDialog({
                    value: {
                        kind: "custom",
                        title: CENTER_TEXTS.archiveTitle,
                        message: CENTER_TEXTS.archiveMessage,
                        confirmLabel: CENTER_TEXTS.archiveConfirm,
                        cancelLabel: CENTER_TEXTS.cancel,
                    },
                    onConfirm: () => {
                        setDialog(null)
                        setSubmitting("archive")
                        call<{ deleted_activity_ids?: unknown[] }>(activitiesService.deleteActivitiesTodo, {
                            routeParams: { student: who.id },
                            data: { activity_ids: [activity.id] },
                        })
                            .then((answer) => {
                                // Un seul `deleteTodo` (l'ancien en envoyait un second par la fiche de gauche).
                                void send(
                                    "deleteTodo",
                                    { id: who.id, activity_ids: answer?.deleted_activity_ids || [] },
                                    who.id!,
                                    "keep"
                                )
                                latest.current.close()
                            })
                            .catch(fail)
                            .finally(() => setSubmitting(null))
                    },
                    onCancel: () => setDialog(null),
                    onDismiss: () => setDialog(null),
                })
            })
            .catch((cause) => {
                setSubmitting(null)
                fail(cause)
            })
    }, [call, fail, send])

    // ── « Modifier le titre » ──────────────────────────────────────────────────────────────────────
    const editTitle = useCallback(() => {
        const activity = latest.current.raw
        setTitle({ value: activity?.material?.title ?? activity?.title ?? "", saving: false })
    }, [])
    const saveTitle = useCallback(() => {
        const activity = latest.current.raw
        if (title == null || activity?.id == null) return
        const trimmed = title.value.trim()
        if (trimmed === "") {
            latest.current.notify(CENTER_TEXTS.emptyTitle, "danger")
            return
        }
        setTitle({ ...title, saving: true })
        call<Record<string, unknown>>(activitiesService.updateActivityTodo, {
            routeParams: { id: activity.id },
            data: { material_title: trimmed },
        })
            .then((answer) => {
                latest.current.notify(CENTER_TEXTS.titleSaved, "success")
                const studentId = activity.student_id != null ? Number(activity.student_id) : null
                if (studentId != null) void send("newTodo", { id: studentId, activity: answer }, studentId, "keep")
                setTitle(null)
            })
            .catch((cause) => {
                setTitle((was) => (was != null ? { ...was, saving: false } : was))
                fail(cause)
            })
    }, [call, fail, send, title])

    // ── « Quiz d'abord · Retirer » ─────────────────────────────────────────────────────────────────
    const removeQuiz = useCallback(() => {
        const { raw: activity, student: who, room: salle } = latest.current
        if (removing || activity?.id == null) return
        setRemoving(true)
        // La vue du masteur se relit tout de suite, sans attendre que la salle ait reçu le message.
        const reread = () => {
            if (salle.liveSessionId != null) {
                call(liveServiceV2.getLiveSessionV2, { routeParams: { id: salle.liveSessionId } }).catch(
                    () => undefined
                )
            } else if (who?.id != null) {
                call(studentsService.getStudent, { routeParams: { id: who.id } }).catch(() => undefined)
            }
        }
        const name = who?.display_name || "l'élève"
        call(activitiesService.deletePrerequisiteQuiz, { routeParams: { activityId: activity.id } })
            .then(async () => {
                reread()
                const told =
                    salle.liveSessionId != null && who?.id != null
                        ? await send("newTodo", { id: who.id, activity: { id: activity.id } }, who.id, "keep")
                        : null
                latest.current.notify(
                    told?.ok === false ? CENTER_TEXTS.quizRemovedLater(name) : CENTER_TEXTS.quizRemoved(name),
                    "success"
                )
            })
            .catch((cause) => {
                reread()
                latest.current.notify(apiFailureOf(cause)?.message || CENTER_TEXTS.quizRemoveFailed, "danger")
            })
            .finally(() => setRemoving(false))
    }, [call, removing, send])

    // ── L'activité ouverte, de quoi en faire une semblable (« + Activité », « /similaire ») ; `null` sans support ──
    const similarSource = useCallback((): TutorLiveGenerateSource | null => {
        const { raw: activity, student: who } = latest.current
        if (activity?.id == null || who?.id == null || activity.material?.id == null) return null
        return {
            studentId: String(who.id),
            activityId: String(activity.id),
            materialId: activity.material.id,
            title: activity.material.title ?? null,
            level: activity.material.level ?? null,
            confidence: activity.material.structured_data?.analysis?.confidence ?? null,
            subjectId: activity.material.school_subject_id ?? null,
            className: activity.material.class ?? null,
        }
    }, [])
    const onAction = useCallback(
        (action: UITutorActivityAction) => {
            switch (action) {
                case "show":
                    return show()
                case "submit":
                    return submit()
                case "validate":
                    return validate()
                case "redo":
                    return redo()
                case "archive":
                    return archive()
                case "editTitle":
                    return editTitle()
                case "generate":
                case "restore":
                    // Jamais au centre du live : « Restaurer » vit aux archives (C-41), « Générer... » hors live.
                    return undefined
            }
        },
        [archive, editTitle, redo, show, submit, validate]
    )

    // Une autre activité : les boîtes de la précédente se ferment.
    const activityId = raw?.id
    useEffect(() => {
        setDialog(null)
        setTitle(null)
    }, [activityId])

    const sheet =
        title != null
            ? renderTitleEditor({
                  value: title.value,
                  saving: title.saving,
                  onChange: (value) => setTitle((was) => (was != null ? { ...was, value } : was)),
                  onSave: saveTitle,
                  onCancel: () => setTitle(null),
              })
            : null

    return {
        onAction,
        submitting,
        dialog,
        sheet,
        quizFirst: { onRemove: removeQuiz, removing },
        similarSource,
    }
}
